import {
  AdapterIOChainKind,
  type AdapterIoChainRecord,
  AdapterIODirection,
  type AdapterIoResultRecordDetail,
  AdapterIOStep,
  type AdapterPushLinkedObjectRecord,
  BookingStatus,
  BookingStatusLabels
} from '@navio/nsdk';
import type { BadgeVariant } from 'noddi-ui-web';
import {
  ADAPTER_IO_DIRECTION_LABEL_BY_VALUE,
  ADAPTER_IO_ENTITY_TYPE_LABEL_BY_VALUE,
  ADAPTER_IO_STEP_LABEL_BY_VALUE,
  ADAPTER_TYPE_LABEL_BY_VALUE,
  type AdapterEnv,
  asAdapterEnv,
  formatConstantCaseToSentenceCase,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';

/** Link roles from the backend that belong on a given I/O step. */
const STEP_LINK_ROLES: Record<AdapterIOStep, readonly string[]> = {
  [AdapterIOStep.PRE_CHECK]: [],
  [AdapterIOStep.FETCH]: [],
  [AdapterIOStep.CUSTOMER]: ['customer', 'external_customer'],
  [AdapterIOStep.CAR]: ['car', 'external_car'],
  [AdapterIOStep.BOOKING]: ['booking', 'external_booking', 'external_booking_public'],
  // Create/update chains: original external sale order (ORDER_ID).
  [AdapterIOStep.ORDER]: ['order', 'external_order'],
  [AdapterIOStep.PAYMENT]: [],
  [AdapterIOStep.INVOICE]: ['external_invoice'],
  // Credit-note repair: external credit order (CREDIT_ORDER_ID) + credit invoice.
  [AdapterIOStep.CREDIT]: ['order', 'external_credit_order'],
  [AdapterIOStep.COMMENT]: [],
  [AdapterIOStep.TIRE]: ['product', 'external_product'],
  [AdapterIOStep.RIM]: ['rim', 'external_rim'],
  [AdapterIOStep.TREAD_DEPTH]: ['tread_depth', 'external_tread_depth'],
  [AdapterIOStep.TYRE_HOTEL]: ['tyre_hotel', 'external_tyre_hotel'],
  // Lifecycle cancel settling the wheel change finalize opened; it carries no links of its own.
  [AdapterIOStep.WHEEL_CHANGE]: [],
  [AdapterIOStep.WHEEL_INSPECTION]: ['wheel_inspection', 'external_wheel_inspection'],
  [AdapterIOStep.STORAGE_FACILITY]: [],
  [AdapterIOStep.STORAGE_OBJECT_GROUP]: [],
  [AdapterIOStep.WHEEL_SET_STOCK]: [],
  [AdapterIOStep.WMS_STOCK]: []
};

/**
 * Refund chains reuse the ORDER step for the external credit order
 * (CREDIT_ORDER_ID). Deep-link that, not the booking's original ORDER_ID.
 */
const REFUND_ORDER_LINK_ROLES = ['order', 'external_credit_order'] as const;

function rolesForStep(step: AdapterIOStep, lifecycleAction?: string | null): readonly string[] {
  if (step === AdapterIOStep.ORDER && lifecycleAction === AdapterIOChainKind.REFUND) {
    return REFUND_ORDER_LINK_ROLES;
  }
  return STEP_LINK_ROLES[step] ?? [];
}

export function linksForStep(
  step: AdapterIOStep,
  links: AdapterPushLinkedObjectRecord[],
  lifecycleAction?: string | null
): AdapterPushLinkedObjectRecord[] {
  const roles = rolesForStep(step, lifecycleAction);
  // Prefer credit-order remote first so "Open remote" hits the refund
  // mirror, not the original sale order (backend list order is create then credit).
  const filtered = links.filter((link) => roles.includes(link.role));
  if (step === AdapterIOStep.ORDER && lifecycleAction === AdapterIOChainKind.REFUND) {
    return [...filtered].sort((a, b) => {
      if (a.role === 'external_credit_order') {
        return -1;
      }
      if (b.role === 'external_credit_order') {
        return 1;
      }
      return 0;
    });
  }
  return filtered;
}

export function unmatchedLinks(links: AdapterPushLinkedObjectRecord[]): AdapterPushLinkedObjectRecord[] {
  const claimed = new Set<string>([...Object.values(STEP_LINK_ROLES).flat(), ...REFUND_ORDER_LINK_ROLES]);
  return links.filter((link) => !claimed.has(link.role));
}

/** Prefer the dedicated merge trail; fall back to step summary notes. */
export function getOpenOrderMergeSummary(detail: AdapterIoResultRecordDetail): string | null {
  const merge = detail.openOrderMergeSummary?.trim();
  if (merge) {
    return merge;
  }
  const summary = detail.summary?.trim();
  return summary || null;
}

type StepContextFn = (
  detail: AdapterIoResultRecordDetail,
  lifecycleAction: string | null | undefined,
  adapterLabel: string
) => string | null;

const STEP_CONTEXT: Partial<Record<AdapterIOStep, StepContextFn>> = {
  [AdapterIOStep.FETCH]: (detail) => (detail.externalSubjectId ? `External id ${detail.externalSubjectId}` : null),
  [AdapterIOStep.CUSTOMER]: (detail) => {
    const parts = [detail.userName, detail.userEmail, detail.userPhone].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : null;
  },
  [AdapterIOStep.CAR]: (detail) => detail.licensePlate || null,
  [AdapterIOStep.BOOKING]: (detail, lifecycleAction, adapterLabel) => {
    const parts: string[] = [];
    if (detail.booking?.slug) {
      parts.push(`slug ${detail.booking.slug}`);
    }
    if (lifecycleAction === AdapterIOChainKind.CANCEL) {
      parts.push(`${adapterLabel} cancel lifecycle`);
    } else if (lifecycleAction === AdapterIOChainKind.UPDATE) {
      parts.push(`${adapterLabel} update lifecycle`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  },
  [AdapterIOStep.ORDER]: (detail, lifecycleAction, adapterLabel) => {
    // Refund chains use ORDER step for the external credit order.
    if (lifecycleAction === AdapterIOChainKind.REFUND) {
      return `${adapterLabel} credit order (refund mirror)`;
    }
    const parts: string[] = [];
    if (detail.orderId != null) {
      parts.push(`Order ${detail.orderId}`);
    }
    if (getOpenOrderMergeSummary(detail)) {
      parts.push('open order cancelled & merged');
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }
};

export function stepContextLine(
  step: AdapterIOStep,
  detail: AdapterIoResultRecordDetail,
  lifecycleAction?: string | null
): string | null {
  const formatter = STEP_CONTEXT[step];
  if (!formatter) {
    return null;
  }
  return formatter(detail, lifecycleAction, adapterAndStepLabels(detail).adapterLabel);
}

/** Display name for a user-ish record (resolved-by, chain actor). */
export function formatUserDisplayName(user: {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string | null {
  const fromName = user.name?.trim();
  if (fromName) {
    return fromName;
  }
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fullName) {
    return fullName;
  }
  return user.email ?? null;
}

/** Backend often sends snake_case technical keys as the human label. */
export function humanizeIdentifierLabel(label: string): string {
  return formatConstantCaseToSentenceCase(label.replace(/-/g, '_'));
}

export function bookingStatusBadgeVariant(status: BookingStatus | number): BadgeVariant {
  switch (status) {
    case BookingStatus.COMPLETED:
      return 'success';
    case BookingStatus.CANCELLED:
      return 'destructive';
    case BookingStatus.CONFIRMED:
    case BookingStatus.ASSIGNED_TO_ROUTE:
      return 'default';
    case BookingStatus.ADMIN_CREATED_PAYMENT_REQUESTED:
      return 'warning';
    case BookingStatus.DRAFT:
    case BookingStatus.DRAFT_PAY_BEFORE:
      return 'outline';
    default:
      return 'outline';
  }
}

function bookingStatusLabelFor(status: BookingStatus | number | null): string | null {
  if (status == null) {
    return null;
  }
  return BookingStatusLabels[status as BookingStatus] ?? String(status);
}

/** Who created the booking first: Navio, or the external system we imported it from. */
export type BookingOrigin = 'navio' | 'adapter';

/**
 * Reads the origin off the oldest chain, which the backend returns first. A pull chain means the
 * external system owned the booking and we imported it; a push chain means Navio created it.
 */
export function bookingOriginFromChains(chains: AdapterIoChainRecord[] | undefined): BookingOrigin | null {
  const oldest = chains?.[0];
  if (!oldest) {
    return null;
  }
  return oldest.direction === AdapterIODirection.PULL ? 'adapter' : 'navio';
}

function adapterAndStepLabels(detail: AdapterIoResultRecordDetail) {
  return {
    adapterLabel: ADAPTER_TYPE_LABEL_BY_VALUE[detail.adapterType] ?? detail.adapterType,
    stepLabel: ADAPTER_IO_STEP_LABEL_BY_VALUE[detail.step] ?? detail.step
  };
}

type AdapterIoResultViewModel = {
  adapterLabel: string;
  stepLabel: string;
  directionLabel: string;
  bookingLabel: string;
  entityLabel: string | null;
  adapterEnv: AdapterEnv | null;
  showAdapterEnvBanner: boolean;
  bookingStatus: number | null;
  bookingStatusLabel: string | null;
  isBookingCancelled: boolean;
  isBookingCompleted: boolean;
  cancelSynced: boolean;
  isPull: boolean;
  bookingOriginLabel: string | null;
  resolvedManuallyLabel: string | null;
  adapterEnvBannerTitle: string | null;
  adapterEnvBannerText: string | null;
};

type BuildViewModelInput = {
  detail: AdapterIoResultRecordDetail;
  isProduction: boolean;
  resolvedByLabel: string | null;
};

/** Pure presentation fields for the adapter I/O detail page. */
export function buildAdapterIoResultViewModel({
  detail,
  isProduction,
  resolvedByLabel
}: BuildViewModelInput): AdapterIoResultViewModel {
  const { adapterLabel, stepLabel } = adapterAndStepLabels(detail);
  const directionLabel = ADAPTER_IO_DIRECTION_LABEL_BY_VALUE[detail.direction] ?? detail.direction;
  const entityLabel =
    detail.entityType != null ? (ADAPTER_IO_ENTITY_TYPE_LABEL_BY_VALUE[detail.entityType] ?? detail.entityType) : null;

  const bookingLabel = detail.booking?.slug
    ? detail.booking.slug.toUpperCase()
    : detail.bookingId != null
      ? `#${detail.bookingId}`
      : detail.externalSubjectId
        ? `ext:${detail.externalSubjectId}`
        : `#${detail.id}`;

  const adapterEnv = asAdapterEnv(detail.adapterEnv);
  const bookingStatus = detail.bookingStatus ?? null;
  const bookingStatusLabel = bookingStatusLabelFor(bookingStatus);
  const cancelChain = (detail.chains ?? []).find((chain) => chain.kind === AdapterIOChainKind.CANCEL);
  const cancelStep = (detail.steps ?? []).find((step) => step.lifecycleAction === AdapterIOChainKind.CANCEL);
  const cancelSynced =
    cancelChain != null || cancelStep != null || detail.lifecycleAction === AdapterIOChainKind.CANCEL;
  const isPull = detail.direction === AdapterIODirection.PULL;
  const bookingOrigin = bookingOriginFromChains(detail.chains);

  const resolvedManuallyLabel = detail.resolvedAt
    ? `${noddiFormatDate(detail.resolvedAt, NoddiDateFormats.DATE_TIME)}${
        resolvedByLabel ? ` by ${resolvedByLabel}` : ''
      }`
    : null;

  return {
    adapterLabel,
    stepLabel,
    directionLabel,
    bookingLabel,
    entityLabel,
    adapterEnv,
    showAdapterEnvBanner: !isProduction && Boolean(adapterEnv),
    bookingStatus,
    bookingStatusLabel,
    isBookingCancelled: bookingStatus === BookingStatus.CANCELLED,
    isBookingCompleted: bookingStatus === BookingStatus.COMPLETED,
    cancelSynced,
    isPull,
    bookingOriginLabel:
      bookingOrigin === 'adapter'
        ? `Imported from ${adapterLabel}`
        : bookingOrigin === 'navio'
          ? 'Created in Navio'
          : null,
    resolvedManuallyLabel,
    adapterEnvBannerTitle: adapterEnv
      ? adapterEnv === 'prod'
        ? `Adapter prod environment`
        : `Adapter test environment`
      : null,
    adapterEnvBannerText: adapterEnv
      ? adapterEnv === 'prod'
        ? `Outbound pushes from this backend target the ${adapterLabel} prod API. Prefer adapter test env on non-prod backends.`
        : `Outbound pushes from this backend target the ${adapterLabel} test API.`
      : null
  };
}
